/**
 * Eagle DealPackage assumption engine (D0→D2).
 * Pure functions — no I/O. Scenarios are assumption deltas, never baked $.
 */
import { normalizeBuyerOverhead } from './buyerOverhead.js';
import { buildLiabilitySchedule } from './liabilitySchedule.js';
import {
  isMultiProductPackage,
  monthProductVolumes,
  packageHasGasVolumes,
  statedGasHh,
} from './productStreams.js';

/** On-screen name for the rate irrAtBid returns. */
export const IRR_NOMINAL_LABEL = 'IRR (nominal annual, monthly compounding)';

/** Effective annual rate (1 + nominal/12)^12 − 1. The stored IRR stays nominal. */
export function effectiveAnnualFromNominal(nominal) {
  if (nominal == null || !Number.isFinite(Number(nominal))) return null;
  const eff = (1 + Number(nominal) / 12) ** 12 - 1;
  return Number.isFinite(eff) ? eff : null;
}

/** Monthly NPV of cashflows at annual discount rate. */
export function npv(cashflows, annualRate) {
  const r = annualRate / 12;
  let total = 0;
  for (let t = 0; t < cashflows.length; t += 1) {
    total += cashflows[t] / (1 + r) ** (t + 1);
  }
  return total;
}

/**
 * IRR of a cashflow series (period rate). Returns null if no root.
 * Prefers annual series for long oil & gas streams. Handles late negative
 * years (e.g. fixed overhead > declining CF) by bracketing from r=0.
 */
export function irr(cashflows, { maxIter = 100 } = {}) {
  if (!cashflows?.length) return null;
  let neg = false;
  let pos = false;
  for (const c of cashflows) {
    if (c < 0) neg = true;
    if (c > 0) pos = true;
  }
  if (!neg || !pos) return null;

  const npvAt = (r) => {
    let total = 0;
    for (let t = 0; t < cashflows.length; t += 1) {
      const denom = (1 + r) ** t;
      if (!Number.isFinite(denom) || denom === 0) return NaN;
      total += cashflows[t] / denom;
    }
    return total;
  };

  const f0 = npvAt(0);
  if (!Number.isFinite(f0)) return null;
  if (Math.abs(f0) < 1) return 0;

  let lo;
  let hi;
  let fLo;
  let fHi;
  if (f0 > 0) {
    // Undisc CF > |outflow| → positive IRR; search (0, hi]
    lo = 0;
    fLo = f0;
    hi = 0.25;
    fHi = npvAt(hi);
    for (let k = 0; k < 40 && Number.isFinite(fHi) && fHi > 0; k += 1) {
      hi += 0.25;
      fHi = npvAt(hi);
      if (hi > 10) break;
    }
  } else {
    // Never recovers undisc → negative IRR; search [-0.99, 0)
    hi = 0;
    fHi = f0;
    lo = -0.25;
    fLo = npvAt(lo);
    for (let k = 0; k < 20 && Number.isFinite(fLo) && fLo < 0; k += 1) {
      lo = (lo - 1) / 2; // approach -1 from above
      if (lo <= -0.99) {
        lo = -0.99;
        fLo = npvAt(lo);
        break;
      }
      fLo = npvAt(lo);
    }
  }

  if (!Number.isFinite(fLo) || !Number.isFinite(fHi) || fLo * fHi > 0) return null;

  for (let i = 0; i < maxIter; i += 1) {
    const mid = (lo + hi) / 2;
    const fMid = npvAt(mid);
    if (!Number.isFinite(fMid) || Math.abs(fMid) < 1) return mid;
    if (fLo * fMid <= 0) {
      hi = mid;
      fHi = fMid;
    } else {
      lo = mid;
      fLo = fMid;
    }
  }
  return (lo + hi) / 2;
}

/** Roll monthly CF into annual buckets (calendar-agnostic 12-month groups). */
export function toAnnualCashflows(monthly) {
  const years = [];
  for (let i = 0; i < monthly.length; i += 12) {
    let sum = 0;
    for (let j = i; j < Math.min(i + 12, monthly.length); j += 1) sum += monthly[j];
    years.push(sum);
  }
  return years;
}

/**
 * Purchase-price IRR from monthly operating cash flow.
 * Solves the monthly rate on [−Bid, CF1, CF2, …], then reports the nominal
 * annual rate (monthly × 12). That matches PV discounting at "r a year,
 * applied monthly" (CF / (1 + r/12)^(t+1)): paying PV10 prints an IRR of 10%,
 * not the effective (1 + r/12)^12 − 1.
 */
export function irrAtBid(monthly, bidPrice) {
  if (!bidPrice || bidPrice <= 0) return null;
  const monthlyRate = irr([-bidPrice, ...(monthly || [])]);
  if (monthlyRate == null || !Number.isFinite(monthlyRate)) return null;
  const annual = monthlyRate * 12;
  return Number.isFinite(annual) ? annual : null;
}

/**
 * Illustrative buyer G&A runs only through the last month whose operating
 * cash flow (before G&A) is positive. Later months are past the economic
 * limit and do not take the overhead. If no month is positive, G&A is not applied.
 */
export function applyBuyerOverhead(monthly, ohMonthly) {
  const series = Array.isArray(monthly) ? monthly : [];
  if (!(Number(ohMonthly) > 0)) {
    return {
      cashflows: series.slice(),
      lastPositiveIndex: null,
      appliedMonths: 0,
      stoppedEarly: false,
    };
  }
  let last = -1;
  series.forEach((c, i) => {
    if (c > 0) last = i;
  });
  if (last < 0) {
    return {
      cashflows: series.slice(),
      lastPositiveIndex: null,
      appliedMonths: 0,
      stoppedEarly: true,
    };
  }
  return {
    cashflows: series.map((c, i) => (i <= last ? c - ohMonthly : c)),
    lastPositiveIndex: last,
    appliedMonths: last + 1,
    stoppedEarly: last < series.length - 1,
  };
}

/**
 * Build monthly net CF ($) for one asset under assumptions.
 *
 * Single-product (legacy):
 *   Gas: Realized = baseRealized + (hh - hhBase) * passThrough + netbackAdj; volume = netMcf
 *   Oil: Realized = baseRealized + (wti - wtiBase) * passThrough + netbackAdj; volume = netOilBbl
 *        (falls back to hh/hhBase when wti* absent so desk knobs still drive oil packages)
 *
 * Multi-product (Josh P0-E / S10): price each present net independently —
 *   oil / ngl → WTI; casinghead_gas (netMcf) / residue_gas (netResidueMcf) → HH.
 */
function finiteOr(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function isOilStream(months, assumptions, asset) {
  const primary =
    assumptions.primaryProduct ||
    asset?.primaryProduct ||
    assumptions.product ||
    null;
  if (primary === 'oil') return true;
  if (!months?.length) return false;
  return months.some((m) => (Number(m.netOilBbl) || 0) > 0);
}

function monthsHaveMultiProduct(months) {
  if (!months?.length) return false;
  let oil = false;
  let gas = false;
  for (const m of months) {
    const v = monthProductVolumes(m);
    if (v.oil > 0 || v.ngl > 0) oil = true;
    if (v.casinghead_gas > 0 || v.residue_gas > 0) gas = true;
    if (oil && gas) return true;
  }
  return false;
}

/** Default NGL price as a fraction of WTI (matches extract_sedgwick_deal_package). */
const NGL_FRAC_OF_WTI = 0.4;

/** A Henry Hub-sized number must not be used as a WTI price. */
function looksLikeGasPrice(n) {
  return Number.isFinite(n) && n >= 0 && n < 15;
}

function looksLikeOilPrice(n) {
  return Number.isFinite(n) && n >= 15;
}

function oilNetback(assumptions) {
  if (assumptions.oilNetbackAdj != null && assumptions.oilNetbackAdj !== '') {
    return finiteOr(assumptions.oilNetbackAdj, 0);
  }
  return finiteOr(assumptions.netbackAdj, 0);
}

function gasNetback(assumptions) {
  if (assumptions.gasNetbackAdj != null && assumptions.gasNetbackAdj !== '') {
    return finiteOr(assumptions.gasNetbackAdj, 0);
  }
  return finiteOr(assumptions.netbackAdj, 0);
}

function oilRealizedPrice(asset, assumptions) {
  const wtiBase = finiteOr(assumptions.wtiBase ?? assumptions.hhBase, 75);
  const wti = finiteOr(assumptions.wti ?? assumptions.hh, wtiBase);
  const pass = finiteOr(assumptions.hhPassThrough, 1);
  return finiteOr(asset.baseRealized, 0) + (wti - wtiBase) * pass + oilNetback(assumptions);
}

function gasRealizedPrice(asset, assumptions) {
  const hhBase = finiteOr(assumptions.hhBase, 3.42);
  const hh = finiteOr(assumptions.hh, hhBase);
  const pass = finiteOr(assumptions.hhPassThrough, 1);
  const gasBase = finiteOr(asset.gasRealized ?? asset.baseRealizedGas, hhBase);
  return gasBase + (hh - hhBase) * pass + gasNetback(assumptions);
}

function nglRealizedPrice(asset, assumptions) {
  const wtiBase = finiteOr(assumptions.wtiBase ?? assumptions.hhBase, 75);
  const wti = finiteOr(assumptions.wti ?? assumptions.hh, wtiBase);
  if (asset.nglRealized != null && Number.isFinite(Number(asset.nglRealized))) {
    const nglBase = Number(asset.nglRealized);
    // Same rule as the ABS legacy path: hold the seller NGL price at the base
    // deck and move it by the implied fraction of WTI (30/75 = 0.40 on the
    // multi-product fixture), not by the full WTI move and not by a gas netback.
    const frac = wtiBase > 0 ? nglBase / wtiBase : NGL_FRAC_OF_WTI;
    return nglBase + (wti - wtiBase) * frac;
  }
  return wti * NGL_FRAC_OF_WTI;
}

export function assetCashflows(asset, months, assumptions, taxRate) {
  // Allow HH / netback stress to $0; never propagate NaN from empty text inputs.
  const multi = monthsHaveMultiProduct(months) || assumptions.multiProduct === true;
  const oil = isOilStream(months, assumptions, asset);
  const bosAdj = finiteOr(assumptions.boswellVolAdj, 0);
  const volMult = asset.isBoswell || asset.isConcentration ? 1 + bosAdj : 1;
  const loeMult = finiteOr(assumptions.loeMult, 1);
  const scale = finiteOr(asset.cfScale, 1);
  const cfs = [];
  let netMcf = 0;
  let netOilBbl = 0;
  let netResidueMcf = 0;
  let netNglBbl = 0;
  let revenue = 0;
  let loe = 0;
  let realized = 0;

  if (multi) {
    const oilPx = oilRealizedPrice(asset, assumptions);
    const gasPx = gasRealizedPrice(asset, assumptions);
    const nglPx = nglRealizedPrice(asset, assumptions);
    realized = oilPx; // primary/oil realized for callers that read a single scalar
    for (const m of months) {
      const vols = monthProductVolumes(m);
      const oilVol = vols.oil * volMult;
      const casingVol = vols.casinghead_gas * volMult;
      const residueVol = vols.residue_gas * volMult;
      const nglVol = vols.ngl * volMult;
      const rev =
        oilVol * oilPx + casingVol * gasPx + residueVol * gasPx + nglVol * nglPx;
      const loeM = (m.loe || 0) * loeMult;
      const tax = rev * taxRate;
      cfs.push((rev - loeM - tax) * scale);
      netOilBbl += oilVol;
      netMcf += casingVol;
      netResidueMcf += residueVol;
      netNglBbl += nglVol;
      revenue += rev * scale;
      loe += loeM * scale;
    }
    return {
      cfs,
      dates: months.map((m) => String(m.date || m.month || '').slice(0, 7)),
      netMcf,
      netOilBbl,
      netResidueMcf,
      netNglBbl,
      revenue,
      loe,
      realized,
      oilRealized: oilPx,
      gasRealized: gasPx,
      nglRealized: nglPx,
      primaryProduct: oil ? 'oil' : 'gas',
      multiProduct: true,
    };
  }

  const priceBase = oil
    ? finiteOr(assumptions.wtiBase ?? assumptions.hhBase, 75)
    : finiteOr(assumptions.hhBase, 3.42);
  const price = oil
    ? finiteOr(assumptions.wti ?? assumptions.hh, priceBase)
    : finiteOr(assumptions.hh, priceBase);
  const pass = finiteOr(assumptions.hhPassThrough, 1);
  const priceDelta = (price - priceBase) * pass;
  const netbackAdj = oil ? oilNetback(assumptions) : gasNetback(assumptions);
  realized = finiteOr(asset.baseRealized, 0) + priceDelta + netbackAdj;

  for (const m of months) {
    const oilVol = Number(m.netOilBbl) || 0;
    const gasVol = Number(m.netMcf) || 0;
    const n = (oil ? oilVol : gasVol) * volMult;
    const rev = n * realized;
    const loeM = (m.loe || 0) * loeMult;
    const tax = rev * taxRate;
    cfs.push((rev - loeM - tax) * scale);
    if (oil) netOilBbl += n;
    else netMcf += n;
    revenue += rev * scale;
    loe += loeM * scale;
  }
  return {
    cfs,
    dates: months.map((m) => String(m.date || m.month || '').slice(0, 7)),
    netMcf,
    netOilBbl,
    netResidueMcf: 0,
    netNglBbl: 0,
    revenue,
    loe,
    realized,
    primaryProduct: oil ? 'oil' : 'gas',
    multiProduct: false,
  };
}

function sumMonthly(streamsByAsset) {
  const len = Math.max(0, ...streamsByAsset.map((s) => s.length));
  const out = new Array(len).fill(0);
  for (const s of streamsByAsset) {
    for (let i = 0; i < s.length; i += 1) out[i] += s[i];
  }
  return out;
}

/**
 * Package cash flow on a shared calendar. Index-aligning wells that start in
 * different months pulls a later well's cash into an earlier discount period
 * and will not match the ABS engine, which keys on month.
 */
function sumMonthlyDated(parts) {
  const dated = parts.filter((p) => p.dates?.some(Boolean));
  if (!dated.length) return sumMonthly(parts.map((p) => p.cfs));
  const map = new Map();
  for (const p of parts) {
    p.cfs.forEach((cf, i) => {
      const key = p.dates?.[i] || `~${String(i).padStart(4, '0')}`;
      map.set(key, (map.get(key) || 0) + cf);
    });
  }
  return [...map.keys()].sort().map((k) => map.get(k));
}

/**
 * Evaluate package under assumption overrides (absolute field values).
 * Optional bidPriceMM enables purchase-price IRR / MOIC on the bid bridge.
 */
export function evaluateDeal(pkg, assumptionOverrides = {}) {
  const taxRate = pkg.meta.taxRate ?? 0.075;
  const primaryProduct = pkg.meta.primaryProduct || pkg.meta.product || 'gas';
  const multiProduct = isMultiProductPackage(pkg);
  // Any oil package with gas volumes — including trace gas — keeps a real HH.
  // Oil-only packages with no gas still mirror WTI into hh (legacy desk knob).
  const hasGasVolumes = primaryProduct === 'gas' || packageHasGasVolumes(pkg);
  const hhBase = finiteOr(
    primaryProduct === 'gas'
      ? (pkg.meta.hhBase ?? pkg.assumptions?.hh)
      : hasGasVolumes
        ? statedGasHh(pkg)
        : (pkg.meta.hhBase ?? (primaryProduct === 'oil' ? pkg.meta.wtiBase : null)),
    primaryProduct === 'oil' && !hasGasVolumes ? 75 : 3.42,
  );
  const wtiBase = finiteOr(pkg.meta.wtiBase ?? (primaryProduct === 'oil' ? 75 : null), 75);
  const base = {
    ...pkg.assumptions,
    hhBase,
    wtiBase,
    primaryProduct,
    multiProduct,
    ...assumptionOverrides,
  };
  // Coerce desk knobs so empty/NaN text inputs cannot poison PV / IRR.
  // Oil desk historically persisted the price knob as `hh`; map that to WTI so
  // slider / bear stress actually reprice oil packages (package.assumptions.wti
  // must not win over an explicit desk override of only `hh`).
  const oilDeal = primaryProduct === 'oil';
  const overrideHas = (k) =>
    Object.prototype.hasOwnProperty.call(assumptionOverrides, k) &&
    assumptionOverrides[k] !== undefined &&
    assumptionOverrides[k] !== '';
  if (oilDeal && hasGasVolumes) {
    // Oil and gas are priced separately. The under-$15 test only decides
    // whether an HH-only override is a WTI (legacy oil desks). A gas price
    // under $15 is applied to HH and is never dropped.
    const pkgWti = finiteOr(pkg.assumptions?.wti ?? wtiBase, wtiBase);
    const pkgHhRaw = finiteOr(pkg.assumptions?.hh ?? hhBase, hhBase);
    const pkgHh = looksLikeOilPrice(pkgHhRaw) && Math.abs(pkgHhRaw - pkgWti) < 1e-6 ? hhBase : pkgHhRaw;
    const keepGasHh = (candidate) => (looksLikeOilPrice(candidate) ? pkgHh : candidate);
    if (overrideHas('wti') && overrideHas('hh')) {
      base.wti = finiteOr(assumptionOverrides.wti, wtiBase);
      const hhOverride = finiteOr(assumptionOverrides.hh, hhBase);
      // An oil-scale HH next to a WTI is the mirrored oil knob, not a gas price.
      base.hh = looksLikeGasPrice(hhOverride) ? hhOverride : pkgHh;
    } else if (overrideHas('wti')) {
      base.wti = finiteOr(assumptionOverrides.wti, wtiBase);
      base.hh = pkgHh;
    } else if (overrideHas('hh')) {
      const overrideHh = finiteOr(assumptionOverrides.hh, pkgHh);
      if (looksLikeGasPrice(overrideHh) && looksLikeOilPrice(pkgWti)) {
        // Gas-strip number: do not reprice oil. Do apply it as HH.
        base.wti = pkgWti;
        base.hh = overrideHh;
      } else if (looksLikeOilPrice(overrideHh) && Math.abs(pkgHhRaw - pkgWti) <= 1e-6) {
        // Oil-scale HH knob on a package that mirrored WTI into hh: reprice oil only.
        base.wti = overrideHh;
        base.hh = pkgHh;
      } else {
        base.hh = overrideHh;
        base.wti = pkgWti;
      }
    } else {
      base.wti = finiteOr(base.wti ?? pkgWti, wtiBase);
      base.hh = keepGasHh(finiteOr(base.hh, pkgHh));
    }
    if (looksLikeOilPrice(base.hh) && Math.abs(base.hh - base.wti) < 1e-6) base.hh = hhBase;
  } else if (oilDeal) {
    const pkgWti = finiteOr(pkg.assumptions?.wti ?? wtiBase, wtiBase);
    let deskPrice;
    if (overrideHas('wti')) deskPrice = assumptionOverrides.wti;
    else if (overrideHas('hh')) {
      const candidate = finiteOr(assumptionOverrides.hh, NaN);
      // Legacy oil desks stored WTI in `hh` (70, 75, 80). A gas default
      // (3.42 − 0.50 = 2.92) must not become the oil price.
      deskPrice = looksLikeGasPrice(candidate) && looksLikeOilPrice(pkgWti) ? pkgWti : candidate;
    } else deskPrice = base.wti ?? base.hh;
    base.wti = finiteOr(deskPrice, wtiBase);
    if (looksLikeGasPrice(base.wti) && looksLikeOilPrice(wtiBase)) base.wti = wtiBase;
    base.hh = base.wti;
  } else {
    base.hh = finiteOr(base.hh, hhBase);
    base.wti = finiteOr(base.wti ?? base.hh, wtiBase);
  }
  base.netbackAdj = finiteOr(base.netbackAdj, 0);
  base.boswellVolAdj = finiteOr(base.boswellVolAdj, 0);
  base.discountRate = finiteOr(base.discountRate, 0.1);
  base.aroMM = Math.max(0, finiteOr(base.aroMM, 0));
  if (base.bidPriceMM != null && base.bidPriceMM !== '') {
    const bid = Number(base.bidPriceMM);
    base.bidPriceMM = Number.isFinite(bid) ? bid : null;
  } else if (base.bidPriceMM === '') {
    base.bidPriceMM = null;
  }

  const streamMap = Object.fromEntries(pkg.streams.map((s) => [s.propnum, s.months]));
  const assetsOut = [];
  const pricedParts = [];
  const missingStreams = [];

  for (const asset of pkg.assets) {
    const months = streamMap[asset.propnum];
    if (!months?.length) {
      // Skip contributes $0; surface so desks / smoke can catch understated package PV.
      missingStreams.push({
        propnum: asset.propnum,
        lease: asset.lease || null,
        resCat: asset.resCat || null,
      });
      continue;
    }
    const priced = assetCashflows(asset, months, base, taxRate);
    const { cfs, netMcf, netOilBbl, revenue, loe, realized } = priced;
    pricedParts.push({ cfs, dates: priced.dates || [] });
    assetsOut.push({
      propnum: asset.propnum,
      lease: asset.lease,
      resCat: asset.resCat,
      isBoswell: !!asset.isBoswell,
      isConcentration: !!(asset.isConcentration || asset.isBoswell),
      assetPv: npv(cfs, base.discountRate ?? 0.1),
      undisc: cfs.reduce((a, b) => a + b, 0),
      year1Cf: cfs.slice(0, 12).reduce((a, b) => a + b, 0),
      netMcf,
      netOilBbl,
      netResidueMcf: priced.netResidueMcf || 0,
      netNglBbl: priced.netNglBbl || 0,
      revenue,
      loe,
      realized,
      refPv10: asset.ref?.pv10,
    });
  }

  let monthly = sumMonthlyDated(pricedParts);
  const monthlyBeforeOh = monthly;
  const buyerOverhead = normalizeBuyerOverhead(base);
  // Keep legacy mirrors in sync so freezes / CRM still see scalars.
  base.overheadMonthly = buyerOverhead.overheadMonthly;
  base.overheadInCf = buyerOverhead.overheadInCf;
  base.buyerOverheadCategories = buyerOverhead.categories;

  const ohMonthly = buyerOverhead.monthlyIncluded || 0;
  const overheadApplied = applyBuyerOverhead(monthly, ohMonthly);
  monthly = overheadApplied.cashflows;

  const discountRate = base.discountRate ?? 0.1;
  const pv = npv(monthly, discountRate);
  const pvWithoutBuyerOh = npv(monthlyBeforeOh, discountRate);
  const undisc = monthly.reduce((a, b) => a + b, 0);
  const year1Cf = monthly.slice(0, 12).reduce((a, b) => a + b, 0);
  const pdpPv = assetsOut.filter((a) => a.resCat === '1PDP').reduce((s, a) => s + a.assetPv, 0);
  // Asset-level PV excludes buyer OH; package PV applies category OH to the sum stream.
  // Negative asset PVs are never floored to zero here.
  // Concentration share: Eagle Boswell (`isBoswell`) or oil top-well (`isConcentration`).
  const concentrationPv = assetsOut
    .filter((a) => a.isBoswell || a.isConcentration)
    .reduce((s, a) => s + a.assetPv, 0);
  const boswellPv = concentrationPv;
  const assetPvSum = assetsOut.reduce((s, a) => s + a.assetPv, 0);

  const aro = (base.aroMM || 0) * 1e6;
  const bidPriceMM = base.bidPriceMM;
  const bidPrice = bidPriceMM != null ? bidPriceMM * 1e6 : null;
  const bidBridgePv = pv - aro;

  let irrAnnual = null;
  let irrWithoutBuyerOh = null;
  let moic = null;
  if (bidPrice != null && bidPrice > 0) {
    irrAnnual = irrAtBid(monthly, bidPrice);
    irrWithoutBuyerOh = irrAtBid(monthlyBeforeOh, bidPrice);
    const totalIn = monthly.reduce((a, b) => a + b, 0);
    moic = totalIn / bidPrice;
  }

  const pvLabel =
    Math.abs(discountRate - 0.1) < 1e-9 ? 'PV10' : `PV@${(discountRate * 100).toFixed(0)}%`;

  const liabilitySchedule = buildLiabilitySchedule(pkg, {
    evaluatedAssets: assetsOut,
    aroMM: base.aroMM || 0,
  });

  return {
    assumptions: base,
    pvLabel,
    discountRate,
    pv,
    pvMM: pv / 1e6,
    /** @deprecated use pvMM — kept for callers; only equals PV10 when discountRate===10% */
    pv10: pv,
    pv10MM: pv / 1e6,
    pvWithoutBuyerOh,
    pvWithoutBuyerOhMM: pvWithoutBuyerOh / 1e6,
    buyerOhPvDelta: pv - pvWithoutBuyerOh,
    buyerOhPvDeltaMM: (pv - pvWithoutBuyerOh) / 1e6,
    pdpPv10: pdpPv,
    pdpPv10MM: pdpPv / 1e6,
    undiscCf: undisc,
    undiscCfMM: undisc / 1e6,
    ntmCf: year1Cf,
    ntmCfMM: year1Cf / 1e6,
    boswellPv10: boswellPv,
    boswellPv10MM: boswellPv / 1e6,
    boswellShare: pdpPv > 0 ? boswellPv / pdpPv : 0,
    /** Model-basis share: concentration-well model PV ÷ model PDP PV. */
    modelBasisShare: pdpPv > 0 ? boswellPv / pdpPv : 0,
    assetPvSum,
    floorsNegativeAssetPv: false,
    aroMM: base.aroMM || 0,
    bidBridgePvMM: bidBridgePv / 1e6,
    bidBridgePv10MM: bidBridgePv / 1e6,
    bidPriceMM: bidPriceMM ?? null,
    irrAnnual,
    /** Effective annual rate beside the nominal IRR. Nominal stays irrAnnual. */
    irrEffective: effectiveAnnualFromNominal(irrAnnual),
    irrNominalLabel: IRR_NOMINAL_LABEL,
    irrWithoutBuyerOh,
    moic,
    equityValueMM: bidBridgePv / 1e6,
    multiples: {
      x4: (year1Cf / 1e6) * 4,
      x5: (year1Cf / 1e6) * 5,
      x6: (year1Cf / 1e6) * 6,
    },
    buyerOverhead: {
      schemaVersion: buyerOverhead.schemaVersion,
      categories: buyerOverhead.categories,
      monthlyTotal: buyerOverhead.monthlyTotal,
      monthlyIncluded: buyerOverhead.monthlyIncluded,
      classification: buyerOverhead.classification,
      illustrationNote: buyerOverhead.illustrationNote,
      economicLimit:
        'Buyer G&A stops after the last month with positive operating cash flow.',
      appliedMonths: overheadApplied.appliedMonths,
      horizonMonths: monthlyBeforeOh.length,
      stoppedEarly: overheadApplied.stoppedEarly,
    },
    liabilitySchedule,
    assets: assetsOut.sort((a, b) => b.assetPv - a.assetPv),
    calibration: pkg.validation
      ? {
          refPdpPv10MM: pkg.validation.refPdpPv10MM,
          refTotalPv10MM: pkg.validation.refTotalPv10MM,
          modelBasePv10MM: pkg.validation.modelBasePv10MM,
          modelVsRefPdpMM:
            pkg.validation.modelBasePv10MM != null && pkg.validation.refPdpPv10MM != null
              ? +(pkg.validation.modelBasePv10MM - pkg.validation.refPdpPv10MM).toFixed(3)
              : null,
        }
      : null,
    disclosures: {
      streamMethod: pkg.meta?.streamMethod || null,
      calibratedToSellerOneline: true,
    },
    missingStreams,
    missingStreamCount: missingStreams.length,
    hasMissingStreams: missingStreams.length > 0,
  };
}

/**
 * Returns at a candidate bid under base + optional stress deltas.
 */
export function evaluateBid(pkg, { bidPriceMM, baseOverrides = {}, stressDeltas = {} } = {}) {
  const base = evaluateDeal(pkg, { ...baseOverrides, bidPriceMM });
  const stress = evaluateDeal(pkg, { ...baseOverrides, ...stressDeltas, bidPriceMM });
  return { base, stress };
}

/**
 * Evaluate one named package scenario. `baseOverrides` are desk knobs (HH/WTI,
 * vol adj, netback, discount, ARO, overhead, bid); scenario `deltas` win on
 * conflict so absolute package stress cases (e.g. HH −$0.50) stay authoritative.
 */
export function evaluateScenario(pkg, scenario, baseOverrides = {}) {
  const deltas = scenario.deltas || {};
  return {
    id: scenario.id,
    label: scenario.label || scenario.id,
    deltas,
    result: evaluateDeal(pkg, { ...baseOverrides, ...deltas }),
  };
}

/** Evaluate all package scenarios under optional desk `baseOverrides`. */
export function evaluateAllScenarios(pkg, baseOverrides = {}) {
  return (pkg.scenarios || []).map((s) => evaluateScenario(pkg, s, baseOverrides));
}
